/**
 * Customer Invoice Export Utility
 * Generates Excel files for customer invoice data export
 * Combines reported articles from all TGs on an assignment
 */

import * as XLSX from 'xlsx'

export interface CustomerInvoiceExportAssignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  location: string | null
  customer_id: string
  customer_name: string
  customer_wants_rut: boolean
  fortnox_customer_id?: string | null
  // Combined articles from all TGs
  combined_articles: CombinedArticle[]
  // Report notes from each TG: { tg_name, report_text }
  tg_reports: TGReport[]
}

export interface CombinedArticle {
  article_id: string | null  // null för utlägg
  article_name: string
  total_quantity: number
  unit: string | null
  unit_price: number | null // Customer price
  is_rut_applicable?: boolean // RUT tax deduction applicable
  notes?: string
  source?: 'tg' | 'central' // Källa: TG-rapporterad eller centralt rapporterad
  // Utlägg-fält
  is_expense?: boolean
  expense_amount_incl_vat?: number | null
  expense_amount_excl_vat?: number | null
  expense_vat_rate?: number | null
}

export interface TGReport {
  tg_name: string
  report_text: string
}

/**
 * Generate Excel workbook for customer invoice export
 * Creates one worksheet per customer
 */
export function generateCustomerInvoiceExcel(assignments: CustomerInvoiceExportAssignment[]): XLSX.WorkBook {
  // Group assignments by customer
  const byCustomer: Record<string, { customer_name: string; wants_rut: boolean; fortnox_customer_id?: string | null; assignments: CustomerInvoiceExportAssignment[] }> = {}
  for (const assignment of assignments) {
    if (!byCustomer[assignment.customer_id]) {
      byCustomer[assignment.customer_id] = {
        customer_name: assignment.customer_name,
        wants_rut: assignment.customer_wants_rut,
        fortnox_customer_id: assignment.fortnox_customer_id,
        assignments: []
      }
    }
    byCustomer[assignment.customer_id].assignments.push(assignment)
  }

  // Create workbook
  const workbook = XLSX.utils.book_new()

  // Column headers (RUT column added after Artikel, Källa column added at end)
  const headers = ['Uppdragsnr', 'Titel', 'Datum', 'Plats', 'Artikel', 'RUT', 'Antal', 'Enhet', 'Á-pris ex moms', 'Summa ex moms', 'Anteckningar', 'Beskrivning för faktura', 'Källa']

  // Create one sheet per customer
  for (const [, { customer_name, wants_rut, fortnox_customer_id, assignments: customerAssignments }] of Object.entries(byCustomer)) {
    // Build all rows (including headers)
    const allRows: (string | number | null)[][] = [headers]

    // Sort assignments by date
    customerAssignments.sort((a, b) => a.start_date.localeCompare(b.start_date))

    for (const assignment of customerAssignments) {
      const assignmentId = assignment.assignment_number ? `#${assignment.assignment_number}` : ''
      const baseData = [
        assignmentId,
        assignment.title,
        assignment.start_date,
        assignment.location || ''
      ]

      // Add TG report notes FIRST (before articles)
      for (const tgReport of assignment.tg_reports) {
        if (tgReport.report_text) {
          const invoiceDesc = [assignment.start_date, assignment.location || ''].filter(Boolean).join(', ')
          allRows.push([
            ...baseData,
            'Övrig anteckning', // Artikel
            '', // RUT
            '', // Antal
            '', // Enhet
            '', // Á-pris ex moms
            '', // Summa ex moms
            `${tgReport.tg_name}: ${tgReport.report_text}`, // Anteckningar (TG-namn: rapporttext)
            invoiceDesc, // Beskrivning för faktura
            'TG' // Källa
          ])
        }
      }

      // Separate regular articles from expenses
      const regularArticles = assignment.combined_articles.filter(a => !a.is_expense)
      const expenses = assignment.combined_articles.filter(a => a.is_expense)

      // Add combined article rows
      for (const article of regularArticles) {
        const quantity = article.total_quantity || 0
        const unitPrice = article.unit_price || 0
        const lineTotal = quantity * unitPrice

        // Build invoice description: artikelbeskrivning, datum, adress
        const invoiceDesc = [
          article.article_name,
          assignment.start_date,
          assignment.location || ''
        ].filter(Boolean).join(', ')

        allRows.push([
          ...baseData,
          article.article_name,
          article.is_rut_applicable ? 'Ja' : '', // RUT column
          article.total_quantity,
          article.unit || '',
          article.unit_price,
          lineTotal > 0 ? lineTotal : '',
          article.notes || '', // Anteckningar (artikelanteckning)
          invoiceDesc, // Beskrivning för faktura
          article.source === 'central' ? 'Central' : 'TG' // Källa
        ])
      }

      // Add expense rows
      for (const expense of expenses) {
        const amount = expense.expense_amount_excl_vat || 0

        // Build invoice description
        const invoiceDesc = [
          `Utlägg: ${expense.article_name}`,
          assignment.start_date,
          assignment.location || ''
        ].filter(Boolean).join(', ')

        allRows.push([
          ...baseData,
          `Utlägg: ${expense.article_name}`,
          '', // RUT - utlägg är aldrig RUT-berättigade
          1, // Antal alltid 1 för utlägg
          'st',
          amount, // Pris ex moms
          amount, // Summa = pris (antal alltid 1)
          expense.expense_vat_rate !== null ? `Moms: ${expense.expense_vat_rate}%` : '', // Anteckningar
          invoiceDesc, // Beskrivning för faktura
          expense.source === 'central' ? 'Central' : 'TG' // Källa
        ])
      }

      // If no articles and no report notes - still add one row for the assignment
      if (assignment.combined_articles.length === 0 && assignment.tg_reports.filter(r => r.report_text).length === 0) {
        const invoiceDesc = [assignment.title, assignment.start_date, assignment.location || ''].filter(Boolean).join(', ')
        allRows.push([
          ...baseData,
          '', // Artikel
          '', // RUT
          '', // Antal
          '', // Enhet
          '', // Á-pris ex moms
          '', // Summa ex moms
          '', // Anteckningar
          invoiceDesc, // Beskrivning för faktura
          '' // Källa
        ])
      }
    }

    // Add empty row + summary row
    let grandTotal = 0
    for (let row = 1; row < allRows.length; row++) {
      const val = allRows[row][9] // Summa ex moms column
      if (typeof val === 'number') grandTotal += val
    }
    allRows.push([]) // Empty row
    allRows.push([
      '', '', '', '', '', '', '', '',
      'Totalt:',
      grandTotal,
      '', '', ''
    ])

    // Create worksheet from array of arrays
    const worksheet = XLSX.utils.aoa_to_sheet(allRows)

    // Apply number format to price columns (I = Á-pris, J = Summa) - shifted by 1 due to RUT column
    const priceFormat = '#,##0.00 "kr"'
    for (let row = 1; row < allRows.length; row++) {
      const cellI = XLSX.utils.encode_cell({ r: row, c: 8 }) // Á-pris ex moms
      const cellJ = XLSX.utils.encode_cell({ r: row, c: 9 }) // Summa ex moms

      if (worksheet[cellI] && typeof worksheet[cellI].v === 'number') {
        worksheet[cellI].z = priceFormat
      }
      if (worksheet[cellJ] && typeof worksheet[cellJ].v === 'number') {
        worksheet[cellJ].z = priceFormat
      }
    }

    // Set column widths
    worksheet['!cols'] = [
      { wch: 12 },  // Uppdragsnr
      { wch: 30 },  // Titel
      { wch: 12 },  // Datum
      { wch: 25 },  // Plats
      { wch: 20 },  // Artikel
      { wch: 5 },   // RUT
      { wch: 8 },   // Antal
      { wch: 8 },   // Enhet
      { wch: 14 },  // Á-pris ex moms
      { wch: 14 },  // Summa ex moms
      { wch: 40 },  // Anteckningar
      { wch: 50 },  // Beskrivning för faktura
      { wch: 10 }   // Källa
    ]

    // Sanitize sheet name (Excel has restrictions)
    // Format: "FortnoxID (RUT) Kundnamn" — Fortnox ID and RUT first so they're never truncated
    const fortnoxPart = fortnox_customer_id ? `${fortnox_customer_id} ` : ''
    const rutPart = wants_rut ? '(RUT) ' : ''
    const prefix = fortnoxPart + rutPart
    const baseName = customer_name.replace(/[\\/*?[\]:]/g, '') // Remove invalid characters
    const sheetName = (prefix + baseName).slice(0, 31) // Max 31 characters

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Kund')
  }

  return workbook
}

/**
 * Generate Excel workbook for trädgårdsmästare invoice admin export
 * Creates one worksheet per trädgårdsmästare
 */
export interface TGInvoiceAdminExportAssignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  location: string | null
  tg_id: string
  tg_name: string
  report_text: string | null
  reported_articles: ReportedArticleExport[] | null
}

export interface ReportedArticleExport {
  article_id: string | null  // null för utlägg
  article_name: string
  actual_quantity: number | null
  unit: string | null
  unit_price: number | null
  notes?: string
  // Utlägg-fält
  is_expense?: boolean
  expense_amount_incl_vat?: number | null
  expense_amount_excl_vat?: number | null
  expense_vat_rate?: number | null
}

export function generateTGInvoiceAdminExcel(assignments: TGInvoiceAdminExportAssignment[]): XLSX.WorkBook {
  // Group assignments by TG
  const byTG: Record<string, { tg_name: string; assignments: TGInvoiceAdminExportAssignment[] }> = {}
  for (const assignment of assignments) {
    if (!byTG[assignment.tg_id]) {
      byTG[assignment.tg_id] = {
        tg_name: assignment.tg_name,
        assignments: []
      }
    }
    byTG[assignment.tg_id].assignments.push(assignment)
  }

  // Create workbook
  const workbook = XLSX.utils.book_new()

  // Column headers (no TG column since each sheet is per TG)
  const headers = ['Uppdragsnr', 'Titel', 'Datum', 'Plats', 'Artikel', 'Antal', 'Enhet', 'Á-pris ex moms', 'Summa ex moms', 'Anteckningar', 'Beskrivning för faktura']

  // Create one sheet per TG
  for (const [, { tg_name, assignments: tgAssignments }] of Object.entries(byTG)) {
    // Build all rows (including headers)
    const allRows: (string | number | null)[][] = [headers]

    // Sort assignments by date
    tgAssignments.sort((a, b) => a.start_date.localeCompare(b.start_date))

    for (const assignment of tgAssignments) {
      const assignmentId = assignment.assignment_number ? `#${assignment.assignment_number}` : ''
      const baseData = [
        assignmentId,
        assignment.title,
        assignment.start_date,
        assignment.location || ''
      ]

      const articles = assignment.reported_articles || []

      // Add report text as separate row FIRST (before articles)
      if (assignment.report_text) {
        const invoiceDesc = [assignmentId, assignment.start_date, assignment.location || ''].filter(Boolean).join(', ')
        allRows.push([
          ...baseData,
          'Övrig anteckning', // Artikel
          '', // Antal
          '', // Enhet
          '', // Á-pris ex moms
          '', // Summa ex moms
          assignment.report_text, // Anteckningar (rapportanteckning)
          invoiceDesc // Beskrivning för faktura
        ])
      }

      // Separate regular articles from expenses
      const regularArticles = articles.filter(a => !a.is_expense)
      const expenseItems = articles.filter(a => a.is_expense)

      // Add regular article rows
      for (const article of regularArticles) {
        const quantity = article.actual_quantity || 0
        const unitPrice = article.unit_price || 0
        const lineTotal = quantity * unitPrice

        // Build invoice description: id - artikelbeskrivning, datum, adress
        const invoiceDesc = [
          assignmentId ? `${assignmentId} - ${article.article_name}` : article.article_name,
          assignment.start_date,
          assignment.location || ''
        ].filter(Boolean).join(', ')

        allRows.push([
          ...baseData,
          article.article_name,
          article.actual_quantity,
          article.unit || '',
          article.unit_price,
          lineTotal > 0 ? lineTotal : '',
          article.notes || '', // Anteckningar (artikelanteckning)
          invoiceDesc // Beskrivning för faktura
        ])
      }

      // Add expense rows
      for (const expense of expenseItems) {
        const amount = expense.expense_amount_excl_vat || 0

        // Build invoice description
        const invoiceDesc = [
          assignmentId ? `${assignmentId} - Utlägg: ${expense.article_name}` : `Utlägg: ${expense.article_name}`,
          assignment.start_date,
          assignment.location || ''
        ].filter(Boolean).join(', ')

        allRows.push([
          ...baseData,
          `Utlägg: ${expense.article_name}`,
          1, // Antal alltid 1 för utlägg
          'st',
          amount, // Pris ex moms
          amount, // Summa = pris (antal alltid 1)
          expense.expense_vat_rate !== null ? `Moms: ${expense.expense_vat_rate}%` : '', // Anteckningar
          invoiceDesc // Beskrivning för faktura
        ])
      }

      // If no articles and no report text - still add one row for the assignment
      if (articles.length === 0 && !assignment.report_text) {
        const invoiceDesc = [assignmentId, assignment.title, assignment.start_date, assignment.location || ''].filter(Boolean).join(', ')
        allRows.push([
          ...baseData,
          '', // Artikel
          '', // Antal
          '', // Enhet
          '', // Á-pris ex moms
          '', // Summa ex moms
          '', // Anteckningar
          invoiceDesc // Beskrivning för faktura
        ])
      }
    }

    // Add separator and total row
    const grandTotal = allRows.slice(1).reduce((sum, row) => {
      const val = row[8]
      return sum + (typeof val === 'number' ? val : 0)
    }, 0)
    allRows.push(['', '', '', '', '', '', '', '', '', '', ''])
    allRows.push(['', '', '', '', 'TOTALT ex moms', '', '', '', grandTotal, '', ''])

    // Create worksheet from array of arrays
    const worksheet = XLSX.utils.aoa_to_sheet(allRows)

    // Apply number format to price columns (H = Á-pris, I = Summa)
    const priceFormat = '#,##0.00 "kr"'
    for (let row = 1; row < allRows.length; row++) {
      const cellH = XLSX.utils.encode_cell({ r: row, c: 7 }) // Á-pris ex moms
      const cellI = XLSX.utils.encode_cell({ r: row, c: 8 }) // Summa ex moms

      if (worksheet[cellH] && typeof worksheet[cellH].v === 'number') {
        worksheet[cellH].z = priceFormat
      }
      if (worksheet[cellI] && typeof worksheet[cellI].v === 'number') {
        worksheet[cellI].z = priceFormat
      }
    }

    // Set column widths
    worksheet['!cols'] = [
      { wch: 12 },  // Uppdragsnr
      { wch: 30 },  // Titel
      { wch: 12 },  // Datum
      { wch: 25 },  // Plats
      { wch: 20 },  // Artikel
      { wch: 8 },   // Antal
      { wch: 8 },   // Enhet
      { wch: 14 },  // Á-pris ex moms
      { wch: 14 },  // Summa ex moms
      { wch: 40 },  // Anteckningar
      { wch: 50 }   // Beskrivning för faktura
    ]

    // Sanitize sheet name (Excel has restrictions)
    const sheetName = tg_name
      .replace(/[\\/*?[\]:]/g, '') // Remove invalid characters
      .slice(0, 31) // Max 31 characters

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Trädgårdsmästare')
  }

  return workbook
}
